import _pt from 'prop-types'
import React from 'react'
const __reactCreateElement__ = React.createElement
const {
  cloneElement,
  useRef,
  useMemo,
  useCallback,
  useEffect,
  useContext,
} = React
import {useKeycodes} from '@accessible/use-keycode'
import useConditionalFocus from '@accessible/use-conditional-focus'
import useSwitch from '@react-hook/switch'
import useMergedRef from '@react-hook/merged-ref'
import useLayoutEffect from '@react-hook/passive-layout-effect'
import useId from '@accessible/use-id'
import Button from '@accessible/button'
import Portalize from 'react-portalize'
import clsx from 'clsx'

const __DEV__ =
  typeof process !== 'undefined' && process.env.NODE_ENV !== 'production'

// @ts-ignore
export const DisclosureContext = React.createContext({}),
  {Consumer: DisclosureConsumer} = DisclosureContext,
  useDisclosure = () => useContext(DisclosureContext),
  useIsOpen = () => useDisclosure().isOpen,
  useControls = () => {
    const {open, close, toggle} = useDisclosure()
    return {
      open,
      close,
      toggle,
    }
  }
export const Disclosure = ({id, open, defaultOpen, onChange, children}) => {
  // eslint-disable-next-line prefer-const
  let [isOpen, toggle] = useSwitch(defaultOpen)
  isOpen = open === void 0 || open === null ? isOpen : open
  const prevOpen = useRef(isOpen)
  id = useId(id)
  useEffect(() => {
    if (isOpen !== prevOpen.current)
      onChange === null || onChange === void 0 ? void 0 : onChange(isOpen)
    prevOpen.current = isOpen
  }, [isOpen])
  const context = useMemo(
    () => ({
      id,
      open: toggle.on,
      close: toggle.off,
      toggle,
      isOpen,
    }),
    [id, isOpen, toggle.on, toggle.off, toggle]
  )
  return __reactCreateElement__(DisclosureContext.Provider, {
    value: context, // @ts-ignore
    children: typeof children === 'function' ? children(context) : children,
  })
}
Disclosure.propTypes = {
  open: _pt.bool,
  defaultOpen: _pt.bool,
  id: _pt.string,
  onChange: _pt.func,
  children: _pt.oneOfType([
    _pt.node,
    _pt.arrayOf(_pt.node),
    _pt.arrayOf(_pt.element),
    _pt.element,
    _pt.func,
  ]).isRequired,
}

const portalize = (Component, portal) => {
  if (portal === false || portal === void 0 || portal === null) return Component
  const props = {
    children: Component,
  }
  if (typeof portal === 'string') props.container = portal
  else Object.assign(props, portal)
  return __reactCreateElement__(Portalize, props)
}

export const Target = ({
  closeOnEscape = true,
  portal,
  openClass,
  closedClass,
  openStyle,
  closedStyle,
  children,
}) => {
  const {id, isOpen, close} = useDisclosure()
  const prevOpen = useRef(isOpen)
  const ref = useMergedRef(
    // @ts-ignore
    children.ref, // provides the target focus when it is in a new open state
    useConditionalFocus(!prevOpen.current && isOpen, true), // handles closing the modal when the ESC key is pressed
    useKeycodes(
      {
        27: () => closeOnEscape && close(),
      },
      [closeOnEscape, close]
    )
  )
  useLayoutEffect(() => {
    prevOpen.current = isOpen
  }, [isOpen])
  return portalize(
    cloneElement(children, {
      'aria-hidden': '' + !isOpen,
      id,
      className:
        clsx(children.props.className, isOpen ? openClass : closedClass) ||
        void 0,
      style: Object.assign(
        {
          visibility: isOpen ? 'visible' : 'hidden',
        },
        children.props.style,
        isOpen ? openStyle : closedStyle
      ),
      ref,
    }),
    portal
  )
}
Target.propTypes = {
  portal: _pt.oneOfType([_pt.bool, _pt.oneOf([null]), _pt.string, _pt.any]),
  closeOnEscape: _pt.bool,
  openClass: _pt.string,
  closedClass: _pt.string,
  openStyle: _pt.any,
  closedStyle: _pt.any,
  children: _pt.element.isRequired,
}
export const Close = ({children}) => {
  const {close, isOpen, id} = useDisclosure()
  return __reactCreateElement__(
    Button,
    null,
    cloneElement(children, {
      'aria-controls': id,
      'aria-expanded': String(isOpen),
      'aria-label': children.props.hasOwnProperty('aria-label')
        ? children.props['aria-label']
        : 'Close',
      onClick:
        typeof children.props.onClick === 'function'
          ? useCallback(
              e => {
                var _children$props$onCli, _children$props

                close()
                ;(_children$props$onCli = (_children$props = children.props)
                  .onClick) === null || _children$props$onCli === void 0
                  ? void 0
                  : _children$props$onCli.call(_children$props, e)
              },
              [children.props.onClick, close]
            )
          : close,
    })
  )
}
Close.propTypes = {
  children: _pt.oneOfType([_pt.element, _pt.element]).isRequired,
}
export const Trigger = ({
  openClass,
  closedClass,
  openStyle,
  closedStyle,
  children,
}) => {
  const {isOpen, id, toggle} = useDisclosure()
  const prevOpen = useRef(isOpen)
  const ref = useMergedRef(
    // @ts-ignore
    children.ref,
    useConditionalFocus(prevOpen.current && !isOpen, true)
  )
  useLayoutEffect(() => {
    prevOpen.current = isOpen
  }, [isOpen])
  return __reactCreateElement__(
    Button,
    null,
    cloneElement(children, {
      'aria-controls': id,
      'aria-expanded': String(isOpen),
      className:
        clsx(children.props.className, isOpen ? openClass : closedClass) ||
        void 0,
      style: Object.assign(
        {},
        children.props.style,
        isOpen ? openStyle : closedStyle
      ),
      onClick:
        typeof children.props.onClick === 'function'
          ? e => {
              toggle()
              children.props.onClick(e)
            }
          : toggle,
      ref,
    })
  )
}
/* istanbul ignore next */

Trigger.propTypes = {
  openClass: _pt.string,
  closedClass: _pt.string,
  openStyle: _pt.any,
  closedStyle: _pt.any,
  children: _pt.oneOfType([_pt.element, _pt.element]).isRequired,
}

if (__DEV__) {
  Disclosure.displayName = 'Disclosure'
  Target.displayName = 'Target'
  Trigger.displayName = 'Trigger'
  Close.displayName = 'Close'
}
